"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, ArrowRight, CircleCheck as CheckCircle2, MapPin, Sparkles, PhoneCall } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqData = [{
  id: "faq-1",
  question: "How long does it take to see Local SEO results for a York-based business?",
  answer: "Typically, you will start seeing noticeable traction within 3 to 6 months. York is a competitive regional market, especially across tourism, professional services, hospitality, and local trades. My strategy focuses on immediate quick-wins like Google Business Profile optimization and localized technical fixes, paired with sustainable long-term authority building."
}, {
  id: "faq-2",
  question: "Why should I work directly with you instead of a York SEO agency?",
  answer: "When you hire an agency, your account is frequently passed to junior executives or account managers playing 'telephone'. When you work with me, you get 100% direct consultant access. I personally analyze your business, craft your strategy, and execute the implementation. You get senior expertise with zero agency fluff or bloated overheads."
}, {
  id: "faq-3",
  question: "How do you optimize my Google Business Profile for the York market?",
  answer: "I conduct a deep local audit: selecting hyper-relevant primary and secondary categories, writing localized keyword-rich descriptions, setting up geotagged imagery, configuring exact services, and establishing automated review acquisition workflows. This drives your profile into the high-converting Google 3-Pack for York searches."
}, {
  id: "faq-4",
  question: "What local SEO strategies work best for York's tourist and hospitality sector?",
  answer: "With over 8 million annual visitors in York, local intent splits into resident services and tourist discovery. I build strategies that capture both: targeting high-intent local queries for residents while optimizing for seasonal visitors searching for boutique stays, dining, historical tours, and experiences prior to arriving."
}, {
  id: "faq-5",
  question: "How do local citations and regional backlinks in Yorkshire impact my rankings?",
  answer: "Google assesses physical context and regional authority. I clean up and secure high-grade local citations across verified Yorkshire business directories, local chambers, and reputable regional publications. Consistent NAP (Name, Address, Phone) data reassures search engines that your business is an established York enterprise."
}, {
  id: "faq-6",
  question: "Can local SEO help my business target nearby areas like Harrogate, Selby, and Malton?",
  answer: "Yes, absolutely. If your service area extends across North Yorkshire, I create dedicated, non-duplicate service area landing pages and geo-targeted schema markup. This allows your website to capture high-intent searches in neighbouring towns without diluting your core York authority."
}, {
  id: "faq-7",
  question: "How does mobile local search affect customers looking for services in York?",
  answer: "Over 65% of local searches in York occur on mobile devices, often with immediate commercial intent (such as 'electrician near York station' or 'caterers in York'). I ensure your website loads instantly, presents click-to-call actions clearly, and passes Google's Core Web Vitals with flying colors."
}, {
  id: "faq-8",
  question: "What is the difference between general SEO and York-focused Local SEO?",
  answer: "General SEO targets broad national keywords regardless of user location. York Local SEO targets searchers specifically in or searching for York. It emphasizes map pack visibility, local entity schema, localized content signals, and geographic customer reviews to convert local searchers into paying clients."
}, {
  id: "faq-9",
  question: "How do you handle multi-location businesses across York and North Yorkshire?",
  answer: "I structure multi-location architecture cleanly by creating individual location pages for each branch, connecting unique Google Business Profiles, adding localized schema markup, and configuring location-specific review collection systems so each branch ranks in its own map pack."
}, {
  id: "faq-10",
  question: "What metrics do you track to prove ROI on York Local SEO campaigns?",
  answer: "I focus on real business outcomes rather than vanity metrics: phone calls, form inquiries, Google Maps directions requests, local postcode keyword rankings, and organic website conversions. You receive clear, transparent monthly reporting showing exactly what work was done and what results were achieved."
}, {
  id: "faq-11",
  question: "How does AI Search (Google AI Overviews & Perplexity) impact York local searches?",
  answer: "AI search engines draw from local brand entities, structured schemas, and prominent local review profiles to formulate answers. By optimizing your structured data and online footprint, I ensure your York business is recommended in modern AI-generated search experience carousels."
}, {
  id: "faq-12",
  question: "How do we get started working together on my York business?",
  answer: "We begin with a straightforward, initial consultation where I inspect your current search presence in York. I will outline quick-wins and a strategic roadmap tailored to your budget and growth targets. You deal directly with me, Yassine Essaadi, from day one."
}];
export default function YorkFaq() {
  const shouldReduceMotion = useReducedMotion();
  const [activeItem, setActiveItem] = useState<string>("faq-1");
  return <section id="yorklocalseofaq" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      {/* Background Glows */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex items-center justify-center gap-2">
            <Badge variant="outline" className="bg-secondary/60 text-secondary-foreground border-border px-3.5 py-1 text-xs font-semibold rounded-full uppercase tracking-wider gap-1.5">
              <MapPin className="size-3.5 text-primary" />
              York Local SEO Guide
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            York Local SEO Questions, <br />
            <span className="text-primary font-bold">Answered Directly by Yassine</span>
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }} className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Clear, transparent answers about growing your business in York and North Yorkshire. No agency jargon, no sales reps—just direct expert advice.
          </motion.p>
        </div>

        {/* FAQ Accordion Box */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3,
        ease: "easeOut"
      }} className="bg-card border border-border/80 rounded-2xl shadow-sm p-4 sm:p-8 lg:p-10 mb-16">
          <Accordion type="single" collapsible value={activeItem} onValueChange={val => setActiveItem(val)} className="w-full space-y-4">
            {faqData.map((faq, i) => <AccordionItem key={faq.id} data-index={i} value={faq.id} className="border border-border/60 rounded-xl px-4 sm:px-6 transition-colors bg-background/50 data-[state=open]:bg-background data-[state=open]:border-primary/40 data-[state=open]:shadow-xs">
                <AccordionTrigger className="py-5 text-left hover:no-underline gap-4 group">
                  <div className="flex items-start sm:items-center gap-3">
                    <HelpCircle className="size-5 text-primary shrink-0 mt-0.5 sm:mt-0 transition-transform group-hover:scale-110" />
                    <span className="text-base sm:text-lg font-semibold text-primary font-default leading-snug">
                      {faq.question}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-5 pt-1 text-muted-foreground text-sm sm:text-base leading-relaxed pl-8 sm:pl-8 border-t border-border/30 mt-1">
                  <p className="pt-2">{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {/* Direct Access CTA Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4,
        ease: "easeOut"
      }} className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground rounded-2xl p-8 sm:p-10 shadow-md flex flex-col lg:flex-row items-center justify-between gap-8 relative overflow-hidden">
          {/* Subtle Grid Pattern Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

          <div className="space-y-3 text-center lg:text-left relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-medium backdrop-blur-xs">
              <Sparkles className="size-3.5 text-accent" />
              <span>Direct Consultant Access</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold font-default text-white">
              Have a specific question about your York business?
            </h3>
            <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed">
              {"You work directly with me, Yassine Essaadi. Book a free 30-minute local SEO strategy call to review your current rankings and competitors in York."}
            </p>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2 text-xs text-primary-foreground/80">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-white" /> No sales pitch
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-white" /> Direct tailored advice
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-white" /> Clear action plan
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full lg:w-auto relative z-10">
            <Button asChild size="lg" className="w-full sm:w-auto bg-white text-primary hover:bg-white/90 font-semibold shadow-sm h-12 px-6 rounded-lg text-base">
              <Link to="/ContactPage">
                <PhoneCall className="size-4 mr-2" />
                Book Free Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 dark:bg-transparent h-12 px-6 rounded-lg text-base">
              <Link to="/CaseStudiesPage">
                View Case Studies
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}